/**
 * Schedules — list with enable Toggle (design/project/lib/dash2.jsx
 * Schedules), bound to the real schedules API. Tapping a card opens the
 * humane ScheduleEditor; the toggle PUTs {enabled} in place.
 */
import type { Schedule } from "../lib/api";
import { timeAgo } from "../lib/format";
import { Card, StatusPill, MonoChip, Toggle, I } from "../kit";
import { SecLabel, EmptyState } from "./DashShell";
import { humanizeCron, cronTime } from "./ScheduleEditor";

export function SchedulesTab({
  schedules,
  err,
  togglingId,
  onToggle,
  onEdit,
  onNew,
}: {
  schedules: Schedule[] | null;
  err: string | null;
  togglingId: string | null;
  onToggle: (s: Schedule) => void;
  onEdit: (s: Schedule) => void;
  onNew: () => void;
}) {
  const active = (schedules ?? []).filter((s) => s.enabled).length;
  return (
    <div className="dsh-stack" style={{ gap: 14 }}>
      <SecLabel
        action={
          <button
            type="button"
            className="dsh-seclabel-action"
            onClick={onNew}
          >
            + new
          </button>
        }
      >
        {schedules === null ? "Schedules" : `Schedules · ${active} active`}
      </SecLabel>

      {err && <p className="dsh-hint-error">Couldn't load schedules ({err})</p>}

      {schedules === null ? (
        !err && <p className="hint">Loading schedules…</p>
      ) : schedules.length === 0 ? (
        <Card>
          <EmptyState
            icon={I.clock}
            title="No schedules yet"
            body="Schedules fire a prompt at an agent on a cron — morning briefs, research sweeps, nightly jobs. Schedule overnight work once you have an agent on a machine."
            cta={
              <button
                type="button"
                className="ns-btn ns-btn--primary"
                onClick={onNew}
              >
                New schedule
              </button>
            }
          />
        </Card>
      ) : (
        <div className="dsh-cards">
          {schedules.map((s) => {
            const human = humanizeCron(s.cron);
            const next = cronTime(s.cron);
            return (
              <Card
                key={s.scheduleId}
                className={`dsh-sched-card${s.enabled ? "" : " paused"}`}
              >
                <div className="dsh-sched-head">
                  <button
                    type="button"
                    className="dsh-sched-main"
                    onClick={() => onEdit(s)}
                  >
                    <div className="dsh-sched-name">{s.name}</div>
                    <div className="dsh-sched-meta">
                      <span className="agent">{s.agentName}</span>
                      <span className="human">{human ?? s.cron}</span>
                    </div>
                  </button>
                  <Toggle
                    on={s.enabled}
                    disabled={togglingId === s.scheduleId}
                    aria-label={
                      s.enabled ? "disable schedule" : "enable schedule"
                    }
                    title={
                      s.enabled
                        ? "enabled — click to pause"
                        : "paused — click to enable"
                    }
                    onClick={() => onToggle(s)}
                  />
                </div>
                <div className="dsh-sched-foot">
                  <MonoChip tone="ghost">{s.cron}</MonoChip>
                  {s.mode && s.mode !== "message" && (
                    <span title="delivery mode (phase 6)">
                      <MonoChip tone="accent">
                        {s.mode}
                        {s.mode === "loop" && s.loopInterval
                          ? ` ${s.loopInterval}`
                          : ""}
                      </MonoChip>
                    </span>
                  )}
                  {/* session-autoClose override set on this schedule */}
                  {s.autoClose && (
                    <span title="closes its session when the task finishes (start fresh next run)">
                      <MonoChip tone="ghost">auto-close</MonoChip>
                    </span>
                  )}
                  {s.lastFiredAt && (
                    <span className="fired">
                      fired {timeAgo(s.lastFiredAt)}
                    </span>
                  )}
                  <span className="right">
                    {s.enabled ? (
                      <StatusPill status="enabled">
                        {next ? `next ${next}` : "enabled"}
                      </StatusPill>
                    ) : (
                      <StatusPill status="paused" />
                    )}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
